/**
 * App-level provider for the Kelly AI surface (ADR 0018). Hosts the single conversation controller
 * (useChatConversations) once and exposes it through two contexts so the streaming conversation surface and
 * the collapsed rail read from the same source without the rail re-rendering per streamed token:
 *   - KellyChatContext  — the full controller, consumed by the conversation surface.
 *   - KellyPanelContext — a memoised slice (thread list + select/new + load flags) for the collapsed rail
 *                         and its select-only hover preview (APP-3397); its deps EXCLUDE messages/liveMessages,
 *                         so its value ref is stable while a turn streams.
 *
 * KellyChatGate mounts it above the account Outlet so a conversation survives page navigation. The gate is
 * NOT a redirect: an un-entitled account gets the whole app, with the controller mounted but idle.
 */

import { createContext, useContext, useEffect, useMemo } from "react"
import type { ComponentType, ReactNode } from "react"
import { useLocation, useMatches } from "react-router-dom"
import { ErrorBoundary } from "@sentry/react"
import { useDispatch, useSelector } from "react-redux"
import { useAccountAlias, useAreEnabledFeaturesResolved, useIsAiTermsAccepted, useIsManagedAgentEnabled } from "resources/account"
import { getModel } from "modules/home-layout/selectors/model"
import { consumeComposerSeed, selectHasActivated, selectPendingComposerSeed } from "../store/agent-chat-slice"
import type { Thread } from "connected-components/ai-teammate/types/thread"
import { KellyAnalyticsProvider } from "../analytics/provider"
import { useChatConversations, type ChatConversationsController } from "./use-chat-conversations"
import { DockOpenerProvider } from "./dock-opener"

// A nudge-click seed should apply within one navigation; anything older is stale.
const COMPOSER_SEED_TTL_MS = 30_000

const KellyChatContext = createContext<ChatConversationsController | null>(null)

export interface KellyPanelValue {
  threads: Thread[]
  activeThreadId: string | null
  /** Terminal-state flag (success OR error) driving the preview's loading row — never hangs on error. */
  isThreadsLoaded: boolean
  isThreadsErrored: boolean
  selectThread: (threadId: string) => void
  startNewThread: () => void
}

// Exported so the preview's stories/tests can supply a controlled value without standing up the whole controller.
export const KellyPanelContext = createContext<KellyPanelValue | null>(null)

interface KellyChatProviderProps {
  children: ReactNode
  /** Streaming is the production default; stories pass "buffered" (see ADR 0007). */
  transportMode?: "streaming" | "buffered"
  /** False keeps the controller mounted but idle — no thread-list fetch. Only KellyChatGate passes it. */
  isActive?: boolean
}

export function KellyChatProvider({ children, transportMode, isActive = true }: KellyChatProviderProps) {
  // The account's global selected risk model (or null), sent in agentArguments only when present (#4125) —
  // read here so the whole app shares one controller instance (index.tsx used to read it per surface).
  const modelId = useSelector(getModel)

  const controller = useChatConversations({ modelId, transportMode, isActive })

  // A lifetime effect, not a mount-time capture: this provider now outlives the navigation the seed rides on,
  // so a mount initialiser would never re-run. The TTL still discards a seed stranded by a page with no
  // provider, or by a reload between the dispatch and this commit.
  const pendingComposerSeed = useSelector(selectPendingComposerSeed)
  const dispatch = useDispatch()
  const { seedComposerDraft } = controller
  useEffect(() => {
    if (pendingComposerSeed == null) return
    dispatch(consumeComposerSeed())
    if (Date.now() - pendingComposerSeed.seededAt <= COMPOSER_SEED_TTL_MS) seedComposerDraft(pendingComposerSeed.text)
  }, [pendingComposerSeed, dispatch, seedComposerDraft])

  const { threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread } = controller
  // Deps deliberately exclude messages/liveMessages so the rail never re-renders per streamed token.
  const panelValue = useMemo<KellyPanelValue>(
    () => ({ threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread }),
    [threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread],
  )

  return (
    <KellyChatContext.Provider value={controller}>
      <KellyPanelContext.Provider value={panelValue}>{children}</KellyPanelContext.Provider>
    </KellyChatContext.Provider>
  )
}

/** The full conversation controller — for the conversation surface. Throws outside the provider. */
export function useKellyChat(): ChatConversationsController {
  const context = useContext(KellyChatContext)
  if (context == null) throw new Error("useKellyChat must be used within a KellyChatProvider")
  return context
}

/**
 * The controller if one is mounted, else null. **A non-null controller does not imply entitlement** — the gate
 * mounts an idle controller on every app page for every account, so this is null only on the non-app sections
 * (and outside a provider entirely). Gate anything a user can SEE on `useKellyChatEntitlement()`, never on this.
 */
export function useKellyChatOptional(): ChatConversationsController | null {
  return useContext(KellyChatContext)
}

/**
 * The transcript surface's presence. The controller outlives it now (ADR 0018), so the surface has to say when
 * it comes and goes: the MUI runtime aborts the in-flight turn on ChatBox unmount, and the per-thread resume
 * latch means a return to the same thread would never re-probe for the stream that turn left running.
 */
export function useKellyChatSurfacePresence(): void {
  const { attachSurface, detachSurface } = useKellyChat()
  useEffect(() => {
    // Yielded so the seq bump is not a synchronous setState inside the effect (react-hooks/set-state-in-effect).
    // Detach only if the attach actually ran, so the controller's surface count stays exact — it is what stops
    // one of two coexisting surfaces (the WP3/WP4 dock-plus-route window) aborting the other's turn.
    let hasAttached = false
    let isMounted = true
    void Promise.resolve().then(() => {
      if (!isMounted) return
      hasAttached = true
      attachSurface()
    })
    return () => {
      isMounted = false
      if (hasAttached) detachSurface()
    }
  }, [attachSurface, detachSurface])
}

/** The panel-content slice — for the collapsed rail and its hover preview. Throws outside the provider. */
export function useKellyPanel(): KellyPanelValue {
  const context = useContext(KellyPanelContext)
  if (context == null) throw new Error("useKellyPanel must be used within a KellyChatProvider")
  return context
}

/** A route declaring `handle={{ kellyHost: false }}` — a section of the account subtree that is not the app. */
function optsOutOfKellyHost(match: { handle: unknown }): boolean {
  const { handle } = match
  return typeof handle === "object" && handle !== null && (handle as { kellyHost?: unknown }).kellyHost === false
}

/**
 * Whether this location is an in-app page Kelly may be hosted on. Two ways it is not: the account root itself,
 * which has no page below it, and a route that opts out — today the two pre-activation shells and the AI-summary
 * popout. Kelly has no host on those, and AccountRoot's resources are not even fetched on the first two.
 *
 * The opt-out is declared on the ROUTE and read back off `useMatches()`, so renaming a path carries its own
 * exclusion with it; a list of path strings kept here would drift silently. The scan is index-free, so a
 * pathless layout route inserted above the account route — the obvious way to add a router-level error
 * boundary — cannot shift it, which is what `matches[0]`/`matches[1]` derivation would have done.
 */
function useIsKellyHostRoute(accountAlias: string): boolean {
  const { pathname } = useLocation()
  const matches = useMatches()
  const segments = pathname.split("/").filter(Boolean)
  if (segments[0] !== accountAlias || segments.length < 2) return false
  return !matches.some(optsOutOfKellyHost)
}

/**
 * Kelly's entitlement, tracking the two REDIRECTING gates the `/kelly-ai` route still carries. Three states
 * rather than a boolean, because the two gates disagree about what "not yet known" means:
 *   - `"confirmed"` — this account has Kelly. The only state that may make a STICKY commitment (activation)
 *     or render anything the user can see.
 *   - `"unknown"`   — the feature list has not arrived. `withFeatureGate` passes through that window, so the
 *     controller must too, or a cold deep link would idle where terms landed and features had not.
 *   - `"denied"`    — nothing runs.
 *
 * **Exact for features, deliberately fail-closed for terms.** `useIsAiTermsAccepted()` collapses "declined"
 * and "not yet resolved" into `false`, while `withAiTermsGate` holds the route on `<Loading />` until the
 * resource settles — so an unresolved terms resource reads `"denied"` here and "wait" there. That is the safe
 * direction (the worst case is Kelly appearing one round trip late on a cold load) and it is not worth an
 * `unknown` terms branch: `hasActivated` is still true from the previous account during a switch, so the branch
 * would fire a thread-list fetch for a user whose terms have not resolved. Explicitly declined, not overlooked.
 */
export type KellyChatEntitlement = "confirmed" | "unknown" | "denied"

export function useKellyChatEntitlement(): KellyChatEntitlement {
  const isAiTermsAccepted = useIsAiTermsAccepted()
  const isManagedAgentEnabled = useIsManagedAgentEnabled()
  const areEnabledFeaturesResolved = useAreEnabledFeaturesResolved()
  if (!isAiTermsAccepted) return "denied"
  if (!areEnabledFeaturesResolved) return "unknown"
  return isManagedAgentEnabled ? "confirmed" : "denied"
}

/**
 * Mounts the controller above the account Outlet so a conversation survives page navigation (ADR 0018).
 * Non-redirecting by design: entitlement decides whether the controller does any WORK, never whether the
 * app renders — and never the shape of the tree above `children`, which would remount every page under it
 * the moment the feature list resolves.
 */
export function KellyChatGate({ children, accountAlias }: { children: ReactNode, accountAlias: string }) {
  const isHostRoute = useIsKellyHostRoute(accountAlias)
  const entitlement = useKellyChatEntitlement()
  const hasActivated = useSelector(selectHasActivated)

  // Above every branch, so the launchers (which gate on entitlement, not on a controller) and the dock share one
  // opener ref even on the routes and the caught-crash fallback where no controller is mounted. The analytics
  // surface is hoisted for the same reason plus one more: it must also cover KellyChatProvider's OWN body,
  // which is where the send event's hook runs.
  if (!isHostRoute) {
    return (
      <DockOpenerProvider>
        <KellyAnalyticsProvider>{children}</KellyAnalyticsProvider>
      </DockOpenerProvider>
    )
  }
  // The boundary is keyed on the ROUTE param, not the loaded alias: `global.selected.account` lags a `/meta`
  // round trip, so keying on it would leave the previous account's controller under the new account's pages
  // (ADR 0015), and the key means a crash also clears on account switch. Kelly is one feature and must not be
  // able to take the app down from AccountRoot — ADR 0013 already caught this class once.
  return (
    <DockOpenerProvider>
      <KellyAnalyticsProvider>
        {/* Outside the boundary: the fallback renders `children` bare, and the launchers in them still track. */}
        <ErrorBoundary key={accountAlias} fallback={<>{children}</>}>
          <KellyChatProvider isActive={entitlement !== "denied" && hasActivated}>
            {children}
          </KellyChatProvider>
        </ErrorBoundary>
      </KellyAnalyticsProvider>
    </DockOpenerProvider>
  )
}

/** Wrap a component so the provider mounts around it — stories and standalone mounts; routes use KellyChatGate. */
export function withKellyChatProvider<Props extends object>(Component: ComponentType<Props>): ComponentType<Props> {
  function WithKellyChatProvider(props: Props) {
    // Keyed on the account: an in-place alias change would otherwise leave the previous account's draft on
    // screen, since the bridge's restore only fires when the new account's bucket holds one (ADR 0015).
    const accountAlias = useAccountAlias()
    return (
      <KellyChatProvider key={accountAlias}>
        <Component {...props} />
      </KellyChatProvider>
    )
  }
  WithKellyChatProvider.displayName = `withKellyChatProvider(${Component.displayName ?? Component.name ?? "Component"})`
  return WithKellyChatProvider
}
